import {
    Table,
    Header,
    Button,
    Box,
    Spinner,
    Alert,
    TableProps,
    SpaceBetween,
    CollectionPreferences,
    TextFilter,
} from "@amzn/awsui-components-react";
import MDATableCell from './MDATableCell';
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import { useCollection } from "@amzn/awsui-collection-hooks";
import React, { useContext } from 'react';
import useLocalStorage from "src/utils/useLocalStorage";
import { DEFAULT_PREFERENCES, PAGE_SIZE_OPTIONS } from "../common/Constants";
import { useMDAExcelData, ExcelRowData, ExcelHeaderData } from "../hooks/useMDAExcelData";
import { MultiAllocationSheetDetails } from "src/models/multidimensional-allocation/MultiAllocationSheetDetails";
import UpdateMDASheetModal from "./UpdateMDASheetModal";
import { MDASheetViewErrorBanner } from "./MDASheetViewErrorBanner";
import FormattingService from "src/services/utils/FormattingService";
import { GlobalAppContext } from "src/components/App";


export interface MDASheetViewTableProps {
    sheetData: MultiAllocationSheetDetails;
}

export default function MDASheetViewTable({ sheetData }: MDASheetViewTableProps) {

    // Extract values from sheetData
    const preSignedUrl = sheetData.preSignedUrl;
    const sheetName = sheetData.sheetName;
    const validationData = sheetData.validationSummary;

    // Use MDA-specific hook for Excel data fetching and validation
    const { excelData, headers, loading, error } = useMDAExcelData(preSignedUrl, validationData);
    const { formattingService } = useContext(GlobalAppContext).services;

    // Create column definitions from headers
    const columnDefinitions: TableProps.ColumnDefinition<ExcelRowData>[] = headers.map((header, index) => ({
        id: `col_${index}`,
        header: mdaColumnHeader(header),
        cell: (item: ExcelRowData) => (mdaRowView(item, index, formattingService)),
        sortingField: `col_${index}`,
    }));

    // Collection preferences with local storage
    const preferencesStorageKey = `mda-sheet-table-preferences`;
    const [preferences, setPreferences] = useLocalStorage.getStorageKeyFromLocalStorage(
        preferencesStorageKey,
        DEFAULT_PREFERENCES
    );

    // Text filtering function for Excel data
    const textFilterFunction = (item: ExcelRowData, filterText: string) => {
        if (!filterText) return true;
        const searchText = filterText.toLowerCase();
        
        // Search in all cell values
        return item.cells.some(cell => {
            if (cell.value === undefined || cell.value === null) return false;
            return String(cell.value).toLowerCase().includes(searchText);
        });
    };

    // Collection hook for filtering and pagination (no sorting)
    const {
        items,
        collectionProps,
        filterProps,
        paginationProps,
    } = useCollection(excelData, {
        filtering: {
            filteringFunction: textFilterFunction,
        },
        pagination: { pageSize: preferences.pageSize },
    });

    // Handle download button click
    const handleDownload = () => {
        const hasErrors = sheetData.validationSummary.validationErrors.length > 0;
        let urlForDownload = hasErrors ? sheetData.validationSummary.preSignedUrl! : preSignedUrl; 

        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = urlForDownload;
        link.download = `${sheetName}.xlsx`
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Show loading state
    if (loading) {
        return (
            <Box textAlign="center" padding="l">
                <Spinner size="large" />
                <Box variant="p">Loading allocation sheet data...</Box>
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Alert type="error" header="Error loading allocation sheet">
                {error}
            </Alert>
        );
    }

    return (<>
        <Table
            {...collectionProps}
            header={<SpaceBetween direction="vertical" size="l"> 
                <Header
                    variant="h2"
                    actions={
                        <SpaceBetween size='s' direction='horizontal'>
                            <Button onClick={handleDownload}> 
                                Download current version 
                            </Button>
                            <UpdateMDASheetModal
                                sheetName={sheetData.sheetName}
                                sheetId={sheetData.sheetId}
                                fileUrl={sheetData.preSignedUrl}
                                status={sheetData.status}
                            />
                        </SpaceBetween>
                    }
                    description={
                        <Box color='text-body-secondary'>
                            {'No edits are allowed in the view mode, edits require uploading a new version which user can do by selecting to upload a new sheet.'}
                        </Box>   
                        }
                >
                    Configure Allocation Sheet
                </Header>
                <MDASheetViewErrorBanner validationSummary={sheetData.validationSummary} />
            </SpaceBetween>
            }
            columnDefinitions={columnDefinitions}
            items={items}
            loading={loading}
            loadingText="Loading allocation data..."
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No allocation details</b>
                    <Box variant="p" color="inherit">
                        {preSignedUrl ?
                            "No allocation details are currently available for this sheet." :
                            "No allocation sheet URL provided."
                        }
                    </Box>
                </Box>
            }
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder="Search allocation data..."
                    filteringAriaLabel="Filter allocation data"
                />
            }
            pagination={
                <Pagination {...paginationProps} />
            }
            preferences={
                <CollectionPreferences
                    title="Preferences"
                    confirmLabel="Confirm"
                    cancelLabel="Cancel"
                    preferences={preferences}
                    onConfirm={({ detail }) => setPreferences(detail)}
                    pageSizePreference={{
                        title: "Page Size",
                        options: PAGE_SIZE_OPTIONS,
                    }}
                    wrapLinesPreference={{
                        label: "Wrap Lines",
                        description: "Select to see all the text and wrap the lines"
                    }}
                    stripedRowsPreference={{
                        label: "Striped Rows",
                        description: "Select to add alternating shaded rows"
                    }}
                />
            }
            wrapLines={preferences.wrapLines}
            stripedRows={preferences.stripedRows}
            sortingDisabled
        />
        </>
    );
}

/** Ratios (numbers) show as percentages; text cells show as they are. */
function formatToPercentage(content: string | number, formattingService: FormattingService): string {
    return typeof content === 'number' ? formattingService.formatPercentage(content) : content;
}

function mdaRowView(row: ExcelRowData, index: number, formattingService: FormattingService) {
    const cell = row.cells[index];
    const cellValue = cell?.value === undefined || cell?.value === null ? '-' : cell.value
    const elem = formatToPercentage(cellValue, formattingService);
    
    return (
        <MDATableCell 
            content={elem} 
            rowError={row.error}
            cellError={cell.error}
        />
    );
}

function mdaColumnHeader(header: ExcelHeaderData) {
    return (
        <MDATableCell 
            content={header.value} 
            cellError={header.error}
        />
    );
}
